import React from "react";
import SimpleTemplate from "@insightfulscience/atomic-react/SimpleTemplate";
import SimpleTemplateTitle from "@insightfulscience/atomic-react/SimpleTemplate/Title";
import PageMeta from "@insightfulscience/smart-react/PageMeta";
import { useLocale } from "@insightfulscience/smart-react/i18";
import Hero from "@insightfulscience/atomic-react/Hero";
import HeroTitle from "@insightfulscience/atomic-react/Hero/Title";
import HeroDescription from "@insightfulscience/atomic-react/Hero/Description";
import HeroAction from "@insightfulscience/atomic-react/Hero/Action";
import { HeroCol, HeroRow } from "@insightfulscience/atomic-react/Hero/Grid";
import HeroButtonLink from "@insightfulscience/atomic-react/Hero/ButtonLink";
import Line from "@insightfulscience/atomic-react/Line";
import LandingSimpleSection from "@insightfulscience/atomic-react/LandingElement/SimpleSection";
import SmartTabs from "@insightfulscience/atomic-react/utils/SmartTabs";
import Tabs from "@insightfulscience/atomic-react/Tabs";
import { TabCol } from "@insightfulscience/atomic-react/Tabs/Grid";
import { Col, Row } from "@insightfulscience/atomic-react/Grid";
import Feature from "@insightfulscience/atomic-react/Feature";
import FeatureImage from "@insightfulscience/atomic-react/Feature/Image";
import FeatureMeta from "@insightfulscience/atomic-react/Feature/Meta";
import Paragraph from "@insightfulscience/atomic-react/Paragraph";
import TabTitle from "@insightfulscience/atomic-react/Tabs/Title";
import pipe from "@insightfulscience/shared-utils/fn/pipe";
import Section from "@insightfulscience/atomic-react/Section";
import Testimonials from "../Home/Testimonials";
import SiteLayout from "../../SiteLayout";
import SectionTryForFree from "../../SectionTryForFree";
import RequestSponsorshipForm from "../../RequestSponsorshipForm";
import WithServices from "../../services";
import withErrorHandling from "../../AppError/withErrorHandling";

const tabImg1 = "/images/geneious/course-licenses/image-3.png";
const tabImg2 = "/images/geneious/igem/cloning.png";
const tabImg3 = "/images/geneious/igem/data-anagement.png";

const Igem = () => {
	const [t] = useLocale("igem");

	return (
		<SiteLayout>
			<PageMeta title={t("META.TITLE")} description={t("META.DESCRIPTION")}>
				<PageMeta.OGP title={t("META.TITLE")} description={t("META.DESCRIPTION")} />
				<PageMeta.Canonical href={t("META.CANONICAL")} />
			</PageMeta>
			<Hero spacing={{ py: 20 }} utility={{ bgColor: "transparent" }}>
				<HeroTitle>Geneious Prime for iGEM Teams</HeroTitle>
				<HeroDescription>
					iGEM teams transform data to discovery, faster with Geneious. Request sponsorship below
					and receive free Geneious Prime licenses for your team.
				</HeroDescription>
				<HeroAction>
					<HeroRow>
						<HeroCol>
							<HeroButtonLink isPreLine href="#request-sponsorship">
								Request Sponsorship
							</HeroButtonLink>
						</HeroCol>
					</HeroRow>
				</HeroAction>
			</Hero>
			<Line />
			<LandingSimpleSection isBorderNone>
				<LandingSimpleSection.TitleV2>
					Unlock the Value of Your Sequence Data
				</LandingSimpleSection.TitleV2>
				<LandingSimpleSection.DescriptionV2
					sx={{ fontColor: "black--custom-1" }}
					spacing={{ mb: 0 }}
				>
					Geneious Prime makes bioinformatics accessible by providing an intuitive, user-friendly
					interface that transforms raw sequence data into meaningful visualizations.
				</LandingSimpleSection.DescriptionV2>
			</LandingSimpleSection>
			<LandingSimpleSection isBorderNone className="py-0">
				<SmartTabs initialActiveTab="tab-1">
					<Tabs isBorderBottom={false} mx>
						<TabCol md="auto">
							<SmartTabs.Tab id="tab-1" Component={TabTitle}>
								Sequence Analysis and Genomics
							</SmartTabs.Tab>
						</TabCol>
						<TabCol md="auto">
							<SmartTabs.Tab id="tab-2" Component={TabTitle}>
								Cloning and Primer Design
							</SmartTabs.Tab>
						</TabCol>
						<TabCol md="auto">
							<SmartTabs.Tab id="tab-3" Component={TabTitle}>
								Data Management and Collaboration
							</SmartTabs.Tab>
						</TabCol>
					</Tabs>

					<Row className="tabs-content">
						<SmartTabs.TabBody.Mounted id="tab-1" Component={Col}>
							<Feature middle className="mt-10">
								<FeatureImage md={8} shadow src={tabImg1} />
								<FeatureMeta md={4}>
									<FeatureMeta.Title>Sequence Analysis and Genomics</FeatureMeta.Title>
									<FeatureMeta.Description>
										<Paragraph sx={{ fontSize: "m" }}>
											User-friendly, intuitive interface packed full of essential genomics tools for
											Sanger, NGS, and long read sequence analysis.
										</Paragraph>
									</FeatureMeta.Description>
								</FeatureMeta>
							</Feature>
						</SmartTabs.TabBody.Mounted>
						<SmartTabs.TabBody.Mounted id="tab-2" Component={Col}>
							<Feature middle className="mt-10">
								<FeatureImage md={8} shadow src={tabImg2} />
								<FeatureMeta md={4}>
									<FeatureMeta.Title>Molecular Cloning and Primer Design</FeatureMeta.Title>
									<FeatureMeta.Description>
										<Paragraph sx={{ fontSize: "m" }}>
											Perform a wide-range of cloning and primer design operations with Geneious
											Prime.
										</Paragraph>
									</FeatureMeta.Description>
								</FeatureMeta>
							</Feature>
						</SmartTabs.TabBody.Mounted>
						<SmartTabs.TabBody.Mounted id="tab-3" Component={Col}>
							<Feature middle className="mt-10">
								<FeatureImage md={8} shadow src={tabImg3} />
								<FeatureMeta md={4}>
									<FeatureMeta.Title>Data Management and Collaboration</FeatureMeta.Title>
									<FeatureMeta.Description>
										<Paragraph sx={{ fontSize: "m" }}>
											Simply drag and drop to import, export, and convert your data. Collaborate
											efficiently with the shared database.
										</Paragraph>
									</FeatureMeta.Description>
								</FeatureMeta>
							</Feature>
						</SmartTabs.TabBody.Mounted>
					</Row>
				</SmartTabs>
			</LandingSimpleSection>
			<Section spacing={{ pt: 10, pb: 20 }}>
				<Testimonials />
			</Section>
			<SimpleTemplate
				id="request-sponsorship"
				utility={{ bgColor: "transparent" }}
				panelUtility={{ radius: 16 }}
				shadow={false}
				paddingTop="pt-0"
			>
				<SimpleTemplateTitle>Request Sponsorship</SimpleTemplateTitle>
				<RequestSponsorshipForm />
			</SimpleTemplate>
			<SectionTryForFree />
		</SiteLayout>
	);
};
export default pipe(Igem, withErrorHandling, WithServices);
